import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import {
  yearFromPeriod,
  yearToPeriod,
} from "../../../components/dashboard/accounting/accountingYearPeriod";
import { fetchAccountingLedger, fetchAccountingImportStatus } from "../../../api/accounting";
import { isLedgerSyncTruncated } from "./ledgerSyncStatus";
import { updateUrlWithException } from "../../../components/Utils";
import {
  ChartBarIcon,
  DocumentTextIcon,
  TableCellsIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

const FETCH_DEBOUNCE_MS = 400;

const formatCurrency = (value) =>
  new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("nb-NO", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

function readInitialController() {
  const sp =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  let voucherTypeIds = [];
  const rawTypes = sp.get("voucher_type_ids");
  if (rawTypes) {
    try {
      voucherTypeIds = JSON.parse(rawTypes);
    } catch {
      voucherTypeIds = rawTypes.split(",").filter(Boolean);
    }
  }

  return {
    year: sp.get("year") ? parseInt(sp.get("year"), 10) : new Date().getFullYear(),
    query: sp.get("query") || "",
    voucherTypeIds,
    page: sp.get("page") ? parseInt(sp.get("page"), 10) : 1,
    per_page: sp.get("per_page") ? parseInt(sp.get("per_page"), 10) : 50,
    sort: sp.get("sort") || "desc",
    sortBy: sp.get("sortBy") || "voucher_date",
  };
}

export const AccountingLedgerPage = () => {
  const { t } = useTranslation();
  const [controller, setController] = useState(readInitialController);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncRequired, setSyncRequired] = useState(false);
  const [importStatus, setImportStatus] = useState(null);

  const debouncedFetchRef = useRef(null);
  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce(async (ctrl) => {
      setLoading(true);
      setError(null);
      setSyncRequired(false);
      try {
        const result = await fetchAccountingLedger({
          year: ctrl.year,
          query: ctrl.query,
          voucherTypeIds: ctrl.voucherTypeIds,
          page: ctrl.page,
          perPage: ctrl.per_page,
          sort: ctrl.sort,
          sortBy: ctrl.sortBy,
        });
        if (result.error) {
          setData(null);
          setSyncRequired(!!result.syncRequired);
          setError(result.data?.errors?.[0] || t("accounting.ledger.load_error"));
          if (result.syncRequired) {
            const statusRes = await fetchAccountingImportStatus();
            if (!statusRes.error) {
              setImportStatus(statusRes.data);
            }
          }
        } else {
          setData(result.data);
        }
      } catch {
        setError(t("accounting.ledger.load_error"));
      } finally {
        setLoading(false);
      }
    }, FETCH_DEBOUNCE_MS);
  }

  useEffect(() => {
    debouncedFetchRef.current(controller);
  }, [controller]);

  useEffect(() => {
    updateUrlWithException({
      year: controller.year,
      page: controller.page,
      per_page: controller.per_page,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
      voucher_type_ids:
        controller.voucherTypeIds?.length > 0
          ? JSON.stringify(controller.voucherTypeIds)
          : "",
    });
  }, [controller]);

  useEffect(() => {
    return () => debouncedFetchRef.current?.cancel();
  }, []);

  const voucherTypeOptions = useMemo(
    () =>
      (data?.voucher_types || []).map((row) => ({
        value: row.id?.toString(),
        label: row.name || row.type || row.id,
      })),
    [data?.voucher_types]
  );

  const period = useMemo(() => yearToPeriod(controller.year), [controller.year]);

  const handlePeriodChange = useCallback((nextPeriod) => {
    const year = yearFromPeriod(nextPeriod);
    setController((prev) => ({ ...prev, page: 1, year }));
  }, []);

  const handleModuleFilterChange = useCallback((filters) => {
    setController((prev) => ({
      ...prev,
      page: 1,
      query: filters.query || "",
      voucherTypeIds: filters.voucherTypes || [],
    }));
  }, []);

  const getInitialFilters = useCallback(
    () => ({
      query: controller.query || "",
      voucherTypes: controller.voucherTypeIds || [],
    }),
    [controller]
  );

  const handleSort = (field) => {
    setController((prev) => ({
      ...prev,
      page: 1,
      sortBy: field,
      sort: prev.sortBy === field && prev.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (page) => {
    setController((prev) => ({ ...prev, page }));
  };

  const pagination = data?.pagination;
  const totalPages = pagination?.total_pages || 1;

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader title={t("accounting.menu.ledger")} />

      <div className="mx-auto max-w-7xl w-full px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <PeriodSelector
                periodType={period.periodType}
                from={period.from}
                to={period.to}
                allowedPeriodTypes={["year"]}
                onChange={handlePeriodChange}
              />
            </div>
            <ModuleFilter
              disabled={loading}
              searchPlaceholder={t("accounting.ledger.search_placeholder")}
              onFiltersChange={handleModuleFilterChange}
              initialFilters={getInitialFilters()}
              hideStatusFilter
              customDropdownFilters={[
                {
                  key: "voucherTypes",
                  label: t("accounting.ledger.voucher_type"),
                  options: voucherTypeOptions,
                },
              ]}
            />
          </div>

      {data?.synced_at && (
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <ArrowPathIcon className="h-4 w-4 text-slate-400" />
          <span>
            {t("accounting.ledger.last_synced", {
              date: formatDateTime(data.synced_at),
            })}
          </span>
          <span className="text-slate-400">·</span>
          <span>{t("accounting.ledger.source_local")}</span>
        </div>
      )}

      {isLedgerSyncTruncated(data) && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="status">
          {t("accounting.ledger.vouchers_truncated")}
        </div>
      )}

      {syncRequired && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-2">
          <p>{t("accounting.ledger.sync_required_hint")}</p>
          {importStatus?.ledger_sync?.created_at && (
            <p className="text-xs text-amber-800">
              {t("accounting.ledger.last_sync_attempt", {
                date: formatDateTime(importStatus.ledger_sync.created_at),
              })}
            </p>
          )}
          <p className="text-xs text-amber-800">
            {t("accounting.ledger.sync_via_integrations")}
          </p>
        </div>
      )}

      {loading && !data && (
        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-500">
          {t("accounting.ledger.loading")}
        </div>
      )}

      {error && !loading && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && data && (
        <>
          <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
                <ChartBarIcon className="h-5 w-5 text-blue-500" />
                {t("accounting.ledger.calculations")}
              </div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt>{t("accounting.ledger.tripletex_expenses")}</dt>
                  <dd>{formatCurrency(data.calculations?.tripletex_expenses?.amount_excluding_vat)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>{t("accounting.ledger.tripletex_salary")}</dt>
                  <dd>{formatCurrency(data.calculations?.tripletex_salary?.amount_excluding_vat)}</dd>
                </div>
                <div className="flex justify-between border-t border-gray-100 pt-2 font-medium">
                  <dt>{t("accounting.ledger.promang_supplier_invoices")}</dt>
                  <dd>{formatCurrency(data.calculations?.promang_supplier_invoices?.amount_excluding_vat)}</dd>
                </div>
              </dl>
              <p className="mt-3 text-xs text-gray-500">{data.calculations?.alignment_note}</p>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
                <TableCellsIcon className="h-5 w-5 text-emerald-500" />
                {t("accounting.ledger.ledger_types")}
              </div>
              <p className="mb-3 text-xs text-gray-500">
                {t("accounting.ledger.count", { count: data.ledger_types?.length || 0 })}
              </p>
              <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
                {(data.ledger_types || []).map((row) => (
                  <li key={row.id} className="flex justify-between gap-2">
                    <span>{row.name || row.type}</span>
                    <span className="text-gray-500">{row.type}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
                <DocumentTextIcon className="h-5 w-5 text-amber-500" />
                {t("accounting.ledger.voucher_types")}
              </div>
              <p className="mb-3 text-xs text-gray-500">
                {t("accounting.ledger.count", { count: data.voucher_types?.length || 0 })}
              </p>
              <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
                {(data.voucher_types || []).map((row) => (
                  <li key={row.id} className="flex justify-between gap-2">
                    <span>{row.name || row.type}</span>
                    <span className="text-gray-500">{row.type}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("accounting.ledger.vouchers_table")}
              </h3>
              <span className="text-sm text-gray-500 tabular-nums">
                {pagination?.total ?? data.vouchers_count ?? 0}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="px-6 py-3">
                      <button type="button" onClick={() => handleSort("number")} className="font-medium hover:text-gray-900">
                        {t("accounting.ledger.voucher_number")}
                      </button>
                    </th>
                    <th className="px-3 py-3">
                      <button type="button" onClick={() => handleSort("voucher_date")} className="font-medium hover:text-gray-900">
                        {t("accounting.ledger.voucher_date")}
                      </button>
                    </th>
                    <th className="px-3 py-3">{t("accounting.ledger.voucher_type")}</th>
                    <th className="px-3 py-3">{t("accounting.ledger.postings")}</th>
                    <th className="px-3 py-3">{t("accounting.ledger.expense_total")}</th>
                    <th className="px-3 py-3">{t("accounting.ledger.salary_total")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(data.vouchers || []).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                        {t("accounting.ledger.no_vouchers")}
                      </td>
                    </tr>
                  ) : (
                    (data.vouchers || []).map((voucher) => (
                      <tr key={voucher.local_id || voucher.id} className="hover:bg-slate-50/70">
                        <td className="px-6 py-3 font-medium text-gray-900">{voucher.number}</td>
                        <td className="px-3 py-3">{voucher.date}</td>
                        <td className="px-3 py-3">{voucher.voucher_type}</td>
                        <td className="px-3 py-3 tabular-nums">{voucher.postings_count}</td>
                        <td className="px-3 py-3 tabular-nums">{formatCurrency(voucher.expense_total_ex_vat)}</td>
                        <td className="px-3 py-3 tabular-nums">{formatCurrency(voucher.salary_total_ex_vat)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 text-sm text-gray-600">
                <span>
                  {t("accounting.ledger.page_of", {
                    page: pagination?.page || controller.page,
                    total: totalPages,
                  })}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={(pagination?.page || controller.page) <= 1}
                    onClick={() => handlePageChange((pagination?.page || controller.page) - 1)}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40"
                  >
                    {t("accounting.ledger.prev_page")}
                  </button>
                  <button
                    type="button"
                    disabled={(pagination?.page || controller.page) >= totalPages}
                    onClick={() => handlePageChange((pagination?.page || controller.page) + 1)}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40"
                  >
                    {t("accounting.ledger.next_page")}
                  </button>
                </div>
              </div>
            )}
          </section>
        </>
      )}
        </div>
      </div>
    </div>
  );
};

export default AccountingLedgerPage;
